import React, { useState, useEffect } from 'react';
import { mysteryBoxAPI } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { 
  Plus, Trash2, RefreshCw, Save, ChevronUp, ChevronDown, 
  Layers, Target, Coins, Users, CheckCircle, Play
} from 'lucide-react';

const JackpotSequencesManager = ({ jackpot, onUpdate }) => {
  const [sequences, setSequences] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newSequence, setNewSequence] = useState({
    name: '',
    total_chests: '',
    prize_coins: '',
    target_coins: '',
    num_winners: 1
  });
  const [showAddForm, setShowAddForm] = useState(false);

  useEffect(() => {
    loadSequences();
  }, []);

  const loadSequences = async () => {
    setLoading(true);
    try {
      const res = await mysteryBoxAPI.getJackpotSequences();
      setSequences(res.data.sequences || []);
    } catch (error) {
      console.error('Erro ao carregar sequências:', error);
      toast.error('Erro ao carregar sequências');
    } finally {
      setLoading(false);
    }
  };

  const addSequence = async () => {
    if (!newSequence.total_chests || !newSequence.prize_coins || !newSequence.target_coins) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }

    setSaving(true);
    try {
      await mysteryBoxAPI.createJackpotSequence({
        name: newSequence.name || null,
        total_chests: parseInt(newSequence.total_chests),
        prize_coins: parseInt(newSequence.prize_coins),
        target_coins: parseInt(newSequence.target_coins),
        num_winners: parseInt(newSequence.num_winners) || 1
      });
      
      toast.success('Sequência adicionada!');
      setNewSequence({ name: '', total_chests: '', prize_coins: '', target_coins: '', num_winners: 1 });
      setShowAddForm(false);
      loadSequences();
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao adicionar sequência');
    } finally {
      setSaving(false);
    }
  };

  const deleteSequence = async (sequenceId) => {
    if (!window.confirm('Remover esta sequência?')) return;

    try {
      await mysteryBoxAPI.deleteJackpotSequence(sequenceId);
      toast.success('Sequência removida!');
      loadSequences();
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao remover sequência');
    }
  };

  const moveSequence = async (index, direction) => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= sequences.length) return;

    const newOrder = [...sequences];
    [newOrder[index], newOrder[newIndex]] = [newOrder[newIndex], newOrder[index]];
    
    const orderedIds = newOrder.map(s => s.id);
    
    try {
      await mysteryBoxAPI.reorderJackpotSequences(orderedIds);
      setSequences(newOrder);
      toast.success('Ordem atualizada!');
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error('Erro ao reordenar');
      loadSequences();
    }
  };

  const resetProgress = async () => {
    if (!window.confirm('⚠️ RESETAR PROGRESSO?\n\nIsso vai:\n- Zerar baús abertos de todas as sequências\n- Reiniciar da primeira sequência\n- Remover histórico de ganhadores\n\nAs configurações das sequências serão mantidas!')) {
      return;
    }

    try {
      await mysteryBoxAPI.resetJackpotSequencesProgress();
      toast.success('Progresso resetado!');
      loadSequences();
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error('Erro ao resetar progresso');
    }
  };

  const formatNumber = (num) => {
    if (!num) return '0';
    return num.toLocaleString();
  };

  const currentSeqIndex = jackpot?.current_sequence_index || 0;
  const totalChests = sequences.reduce((sum, s) => sum + (s.total_chests || 0), 0);
  const totalOpened = sequences.reduce((sum, s) => sum + (s.chests_opened || 0), 0);
  const totalPrize = sequences.reduce((sum, s) => sum + ((s.total_chests || 0) * (s.prize_coins || 0)), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <RefreshCw className="w-6 h-6 animate-spin text-red-500" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Estatísticas das Sequências */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <div className="p-2 bg-blue-50 rounded-lg text-center">
          <p className="text-xs text-gray-500">Sequências</p>
          <p className="text-lg font-bold text-blue-600">{sequences.length}</p>
        </div>
        <div className="p-2 bg-purple-50 rounded-lg text-center">
          <p className="text-xs text-gray-500">Total Baús</p>
          <p className="text-lg font-bold text-purple-600">{totalChests}</p>
        </div>
        <div className="p-2 bg-green-50 rounded-lg text-center">
          <p className="text-xs text-gray-500">Abertos</p>
          <p className="text-lg font-bold text-green-600">{totalOpened}/{totalChests}</p>
        </div>
        <div className="p-2 bg-yellow-50 rounded-lg text-center">
          <p className="text-xs text-gray-500">Prêmio Total</p>
          <p className="text-sm font-bold text-yellow-600">{formatNumber(totalPrize)} 🪙</p>
        </div>
      </div>

      {/* Lista de Sequências */}
      <div className="space-y-2">
        {sequences.length === 0 ? (
          <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg border-2 border-dashed">
            <Layers className="w-10 h-10 mx-auto mb-2 text-gray-300" />
            <p className="text-sm">Nenhuma sequência configurada</p>
            <p className="text-xs text-gray-400">Adicione sequências para o evento Jackpot</p>
          </div>
        ) : (
          sequences.map((seq, index) => {
            const isActive = index === currentSeqIndex && jackpot?.active;
            const isCompleted = seq.chests_opened >= seq.total_chests;
            const progress = seq.total_chests > 0 
              ? ((seq.chests_opened / seq.total_chests) * 100).toFixed(0) 
              : 0;

            return (
              <div 
                key={seq.id}
                className={`p-3 rounded-lg border-2 transition-all ${
                  isActive 
                    ? 'border-green-500 bg-green-50' 
                    : isCompleted 
                      ? 'border-gray-300 bg-gray-50 opacity-70'
                      : 'border-gray-200 bg-white'
                }`}
              >
                {/* Header da Sequência */}
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-gray-700">#{index + 1}</span>
                    {seq.name && (
                      <span className="text-sm text-gray-600">- {seq.name}</span>
                    )}
                    {isActive && (
                      <Badge className="bg-green-500 text-white animate-pulse text-xs">
                        <Play className="w-3 h-3 mr-1" />
                        ATIVA
                      </Badge>
                    )}
                    {isCompleted && (
                      <Badge className="bg-gray-400 text-white text-xs">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Concluída
                      </Badge>
                    )}
                  </div>
                  
                  {/* Botões de controle */}
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0"
                      onClick={() => moveSequence(index, 'up')}
                      disabled={index === 0}
                    >
                      <ChevronUp className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0"
                      onClick={() => moveSequence(index, 'down')}
                      disabled={index === sequences.length - 1}
                    >
                      <ChevronDown className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                      onClick={() => deleteSequence(seq.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {/* Detalhes da Sequência */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                  <div className="flex items-center gap-1.5 p-1.5 bg-white rounded border">
                    <Layers className="w-3.5 h-3.5 text-purple-500" />
                    <div>
                      <p className="text-gray-400">Baús</p>
                      <p className="font-semibold">{seq.chests_opened}/{seq.total_chests}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 p-1.5 bg-white rounded border">
                    <Coins className="w-3.5 h-3.5 text-yellow-500" />
                    <div>
                      <p className="text-gray-400">Prêmio</p>
                      <p className="font-semibold">{formatNumber(seq.prize_coins)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 p-1.5 bg-white rounded border">
                    <Target className="w-3.5 h-3.5 text-blue-500" />
                    <div>
                      <p className="text-gray-400">Meta</p>
                      <p className="font-semibold">{formatNumber(seq.target_coins)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 p-1.5 bg-white rounded border">
                    <Users className="w-3.5 h-3.5 text-green-500" />
                    <div>
                      <p className="text-gray-400">Ganhadores</p>
                      <p className="font-semibold">{seq.num_winners || 1}</p>
                    </div>
                  </div>
                </div>

                {/* Barra de Progresso */}
                <div className="mt-2">
                  <div className="flex justify-between text-[10px] text-gray-500 mb-1">
                    <span>Progresso da Sequência</span>
                    <span>{progress}%</span>
                  </div>
                  <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                    <div 
                      className={`h-full transition-all duration-300 ${
                        isCompleted ? 'bg-gray-400' : isActive ? 'bg-green-500' : 'bg-blue-500'
                      }`}
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Formulário Adicionar Sequência */}
      {showAddForm ? (
        <Card className="border-2 border-dashed border-red-300">
          <CardHeader className="p-3">
            <CardTitle className="text-sm flex items-center gap-2">
              <Plus className="w-4 h-4" />
              Nova Sequência
            </CardTitle>
          </CardHeader>
          <CardContent className="p-3 pt-0 space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div className="col-span-2">
                <Label className="text-xs">Nome (opcional)</Label>
                <Input
                  className="h-9 text-sm"
                  placeholder="Ex: Fase Final"
                  value={newSequence.name}
                  onChange={e => setNewSequence(prev => ({...prev, name: e.target.value}))}
                />
              </div>
              <div>
                <Label className="text-xs">Quantidade de Baús *</Label>
                <Input
                  type="number"
                  className="h-9 text-sm"
                  placeholder="Ex: 10"
                  min="1"
                  value={newSequence.total_chests}
                  onChange={e => setNewSequence(prev => ({...prev, total_chests: e.target.value}))}
                />
              </div>
              <div>
                <Label className="text-xs">Prêmio por Baú *</Label>
                <Input
                  type="number"
                  className="h-9 text-sm"
                  placeholder="Ex: 50000"
                  min="1"
                  value={newSequence.prize_coins}
                  onChange={e => setNewSequence(prev => ({...prev, prize_coins: e.target.value}))}
                />
              </div>
              <div>
                <Label className="text-xs">Meta de Moedas *</Label>
                <Input
                  type="number"
                  className="h-9 text-sm"
                  placeholder="Ex: 100000000"
                  min="1"
                  value={newSequence.target_coins}
                  onChange={e => setNewSequence(prev => ({...prev, target_coins: e.target.value}))}
                />
              </div>
              <div>
                <Label className="text-xs">Ganhadores (1-10)</Label>
                <Input
                  type="number"
                  className="h-9 text-sm"
                  placeholder="1"
                  min="1"
                  max="10"
                  value={newSequence.num_winners}
                  onChange={e => setNewSequence(prev => ({...prev, num_winners: e.target.value}))}
                />
              </div>
            </div>

            {/* Preview */}
            {newSequence.total_chests && newSequence.prize_coins && (
              <div className="p-2 bg-yellow-50 rounded text-xs text-yellow-700">
                Total da sequência: {formatNumber(parseInt(newSequence.total_chests || 0) * parseInt(newSequence.prize_coins || 0))} moedas
              </div>
            )}

            <div className="flex gap-2 pt-2">
              <Button
                variant="outline"
                className="flex-1 h-9 text-sm"
                onClick={() => {
                  setShowAddForm(false);
                  setNewSequence({ name: '', total_chests: '', prize_coins: '', target_coins: '', num_winners: 1 });
                }}
              >
                Cancelar
              </Button>
              <Button
                className="flex-1 h-9 text-sm bg-red-600 hover:bg-red-700"
                onClick={addSequence}
                disabled={saving}
              >
                {saving ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Plus className="w-4 h-4 mr-1" />
                    Adicionar
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Button
          variant="outline"
          className="w-full h-10 border-2 border-dashed border-red-300 text-red-600 hover:bg-red-50"
          onClick={() => setShowAddForm(true)}
        >
          <Plus className="w-4 h-4 mr-2" />
          Adicionar Sequência
        </Button>
      )}

      {/* Botão Resetar Progresso */}
      {sequences.length > 0 && (
        <Button
          variant="outline"
          className="w-full h-9 text-orange-600 border-orange-300 hover:bg-orange-50 text-sm"
          onClick={resetProgress}
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Resetar Progresso (Manter Configurações)
        </Button>
      )}
    </div>
  );
};

export default JackpotSequencesManager;
